import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Input, Modal, Select, Space, Tag } from 'antd'
import { FileTextOutlined } from '@ant-design/icons'
import {
  errorNotification,
  postRequest,
  successNotification,
  trackingBillOfLadingCompleteUrl,
  trackingDocumentLinkUrl
} from '../../config'
import {
  buildBillOfLadingPayload,
  errorSentence,
  formatDateTime,
  validateBillOfLading
} from './utils'

// Finishing a bill of lading the reader could not (spec: whatsapp-shipment-tracking Req 1.2).
//
// The fields start from whatever the reader DID manage, because most of the time it got four
// things right and missed one — and retyping the other four is how a staff member introduces a
// mistake the reader did not make.
//
// What happens next is the CUSTOMER's own confirm message, not a push to payment. The document is
// the source of truth and the customer is the one holding it, so a reading finished by hand goes
// round the same loop as any other: they see what we read and say whether it is right.
const BillOfLadingModal = ({ visible, row, lines, onClose, onDone }) => {
  const waiting = row || {}
  const reading = waiting.reading || {}
  const [form, setForm] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [opening, setOpening] = useState(null)

  useEffect(() => {
    if (!visible) return
    setForm({
      number: reading.blNumber || reading.number || '',
      lineId: reading.lineId || undefined,
      vessel: reading.vessel || '',
      voyage: reading.voyage || '',
      pol: reading.pol || '',
      pod: reading.pod || '',
      containers: (reading.containers || []).join(', ')
    })
    setSubmitted(false)
    setSaving(false)
    // eslint-disable-next-line
  }, [visible, waiting.conversationId])

  const set = (key, value) => setForm(current => ({ ...current, [key]: value }))

  const errors = submitted ? validateBillOfLading({ ...form, lines }) : []

  const openDocument = async document => {
    const response = await postRequest(
      trackingDocumentLinkUrl(document.id),
      {},
      'document link',
      () => setOpening(document.id)
    )
    setOpening(null)
    if (!response.success) {
      errorNotification(errorSentence(response, 'That document could not be opened.'))
      return
    }
    const url = response.data && response.data.url
    // `noopener,noreferrer` with the comma: `"noopener"` alone is read as the window NAME, which
    // returns null and navigates this tab away instead.
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  const submit = async () => {
    setSubmitted(true)
    if (validateBillOfLading({ ...form, lines }).length) return
    const response = await postRequest(
      trackingBillOfLadingCompleteUrl(waiting.conversationId),
      buildBillOfLadingPayload(form),
      'bill of lading',
      setSaving
    )
    if (!response.success) {
      // Past the 24-hour window the API saves the reading and asks the customer on their next
      // message instead. Nothing is left to do here, so the row leaves the queue with the
      // server's sentence explaining why.
      const code = (((response.error || {}).response || {}).data || {}).code
      if (code === 'outside_window') {
        errorNotification(errorSentence(response, 'Saved. The customer is asked when they next write.'))
        if (onDone) onDone()
        return
      }
      // The server's own sentence where it has one: it names the number or the line to reach for,
      // and it is the sentence a staff member reads out to the customer.
      errorNotification(
        errorSentence(response, 'That could not be sent. Check the number and the line.')
      )
      return
    }
    successNotification('Sent to the customer to confirm.')
    if (onDone) onDone()
  }

  const sorted = (lines || [])
    .slice()
    .sort((a, b) => Number((a || {}).position || 0) - Number((b || {}).position || 0))

  return (
    <Modal
      open={visible}
      title="Finish the bill of lading"
      onCancel={onClose}
      okText="Send to customer to confirm"
      onOk={submit}
      confirmLoading={saving}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap>
          {(waiting.documents || []).length === 0 ? (
            <span style={{ opacity: 0.65 }}>No document was stored.</span>
          ) : null}
          {(waiting.documents || []).map(document =>
            document.deletedAt ? (
              <Tag key={document.id}>
                {`${document.filename || 'Document'} · deleted ${formatDateTime(
                  document.deletedAt
                )}`}
              </Tag>
            ) : (
              <Button
                key={document.id}
                icon={<FileTextOutlined />}
                loading={opening === document.id}
                onClick={() => openDocument(document)}
              >
                {document.filename || 'Open the document'}
              </Button>
            )
          )}
        </Space>

        {(waiting.missing || []).length ? (
          <Alert
            type="info"
            showIcon
            message={`The reader could not get: ${(waiting.missing || []).join(', ')}`}
            description="Everything else below is what it did read — change only what is wrong."
          />
        ) : null}

        {errors.length ? (
          <Alert
            type="error"
            showIcon
            message="Check these before sending"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.map(sentence => (
                  <li key={sentence}>{sentence}</li>
                ))}
              </ul>
            }
          />
        ) : null}

        <div>
          <div style={{ marginBottom: 4 }}>Number on the bill of lading</div>
          <Input
            value={form.number}
            placeholder="MAEU123456789"
            onChange={e => set('number', e.target.value)}
          />
        </div>

        <div>
          <div style={{ marginBottom: 4 }}>Shipping line</div>
          <Select
            showSearch
            optionFilterProp="children"
            placeholder="The line printed at the top of the document"
            style={{ width: '100%' }}
            value={form.lineId}
            onChange={value => set('lineId', value)}
          >
            {sorted.map(line => (
              <Select.Option key={line.id} value={line.id}>
                {line.name}
                {line.scac ? ` · ${line.scac}` : ''}
              </Select.Option>
            ))}
          </Select>
        </div>

        <Space style={{ width: '100%' }}>
          <Input
            addonBefore="Vessel"
            value={form.vessel}
            onChange={e => set('vessel', e.target.value)}
          />
          <Input
            addonBefore="Voyage"
            value={form.voyage}
            onChange={e => set('voyage', e.target.value)}
          />
        </Space>

        <Space style={{ width: '100%' }}>
          <Input
            addonBefore="From"
            value={form.pol}
            onChange={e => set('pol', e.target.value)}
          />
          <Input addonBefore="To" value={form.pod} onChange={e => set('pod', e.target.value)} />
        </Space>

        <div>
          <div style={{ marginBottom: 4 }}>Containers (optional, separated by commas)</div>
          <Input.TextArea
            rows={2}
            value={form.containers}
            placeholder="MSKU1234565"
            onChange={e => set('containers', e.target.value)}
          />
        </div>
      </Space>
    </Modal>
  )
}

BillOfLadingModal.propTypes = {
  visible: PropTypes.bool,
  row: PropTypes.object,
  lines: PropTypes.array,
  onClose: PropTypes.func,
  onDone: PropTypes.func
}

export default BillOfLadingModal
